import * as ReduxUtils from "../../lib/util/ReduxUtils";
import { actionCreator, asyncActionCreators } from "../../lib/util/ReduxUtils";
import store from "../../store";

import * as UserSelectors from "../User/User.selector";
import { selectCurrentScopeFleetId } from "../User/User.selector";
import * as DashboardActions from "../Dashboard/DashboardActions";
import * as FleetSelectors from "../Fleet/Fleet.selector";
import * as FleetActions from "../Fleet/Fleet.action";
import * as GroupsActions from "../Groups/Groups.action";
import * as StorageUtils from "../../lib/util/StorageUtils";
import * as UserUtils from "../../lib/util/UserUtils";
import * as EnvironmentUtils from "../../lib/util/EnvironmentUtils";
import * as InsightsActions from "../Insights/Insights.action";
import * as RewardsActions from "../Rewards/Rewards.action";
import * as Pillars from "../../lib/Pillars";
import { OVERRIDE_FLEET_KEY } from "../../StorageKeys";
import * as UserActions from "../User/User.action";
import { ScopeInfo } from "../../lib/types/MiscTypes";
import * as AuthUtils from "../../lib/util/AuthUtils";
import {
	getEmulatedGroupId,
	setEmulatedGroupId,
} from "../../lib/util/AuthUtils";
import * as Flags from "../../lib/Flags";
import { User } from "../../lib/types/UserTypes";
import API_DEF, { postUserUpdate } from "../../Api";
import { GlobalReducerState } from "./Global.reducer";

export const setMobile = actionCreator<boolean>("[GLOBAL] SET_MOBILE");

export const updateLoadingOverlay = actionCreator<{
	visible: boolean;
	message?: string;
}>("[GLOBAL] UPDATE_LOADING_OVERLAY");

export const setMobileNavVisible = actionCreator<boolean>(
	"[GLOBAL] SET_MOBILE_NAV_VISIBLE",
);

export const getFleetData = asyncActionCreators<
	ScopeInfo & { oldFleetId?: string; forceReload?: boolean },
	void
>("[GLOBAL] GET_FLEET_DATA", async (action, dispatch, getState) => {
	ReduxUtils.batch(() => {
		const state = getState();
		const { oldFleetId, forceReload, ...scope } = action.payload;

		if (scope.fleetId === FleetSelectors.GLOBAL_FLEET_ID) {
			// Global fleet, don't retrieve any data
			return;
		}

		const currentUser = UserSelectors.selectLoggedInUser(state);
		const hasTrends = UserSelectors.selectCurrentScopeHasTrends(state);

		// get insights
		if (UserUtils.isInsightsEnabled(currentUser) && hasTrends) {
			const anonymousMode = Flags.ANONYMOUS_MODE.is();
			dispatch(
				InsightsActions.getInsights({
					...scope,
					anonymousMode: anonymousMode || undefined,
					userId: currentUser?.uid,
				}),
			);
		}

		if (UserUtils.isRewardsEnabled(currentUser)) {
			dispatch(RewardsActions.getGroupCompetitions(scope));
		}

		// get groups
		const fleetMismatch =
			oldFleetId !== undefined && oldFleetId !== scope.fleetId;
		if (
			fleetMismatch ||
			!FleetSelectors.selectCurrentScopeFleet(state).resolved ||
			forceReload
		) {
			dispatch(
				FleetActions.getScopeFleet({
					fleetId: scope.fleetId,
					noCache: forceReload,
					group: scope.group,
				}),
			);
		}
	});
});

export const setScope = actionCreator<
	Omit<ScopeInfo, "fleetId"> & { fleetId: null | undefined | string }
>("[GLOBAL] SET_SCOPE", (action, dispatch, getState) => {
	ReduxUtils.batch(async () => {
		const { fleetId, group, rootGroupId } = action.payload;
		const state = getState();

		let currentFleetId = fleetId || selectCurrentScopeFleetId(state);

		const user = UserSelectors.selectLoggedInUser(state);

		// Reset to default if null
		if (fleetId === null) {
			currentFleetId = user?.fleetId;
		}

		if (!currentFleetId) {
			throw new Error(`Invalid fleet ID, was ${currentFleetId}`);
		}

		dispatch(
			GroupsActions.setScopeGroup({ group: group || null, rootGroupId }),
		);

		if (fleetId !== undefined) {
			await dispatch(
				UserActions.updateUserObject({
					fleetIdOverride: fleetId,
				}),
			);
			StorageUtils.set(OVERRIDE_FLEET_KEY, fleetId);
		}

		dispatch(
			DashboardActions.setComparisonLevels(
				group
					? [Pillars.PILLAR_LEVEL_ROOT, Pillars.PILLAR_LEVEL_GROUP]
					: [Pillars.PILLAR_LEVEL_ROOT],
			),
		);

		dispatch(
			getFleetData({
				fleetId: currentFleetId,
				group: group || null,
				oldFleetId: (user && UserSelectors.selectUserScopeFleetId(user)) as any,
			}),
		);

		if (getEmulatedGroupId() !== undefined) {
			setEmulatedGroupId(null);
		}
	});
});

export const loadData = asyncActionCreators<void, any>(
	"[GLOBAL] LOAD_DATA",
	async (action, dispatch, getState) => {
		const appEnvChanged = EnvironmentUtils.hasAppEnvChanged();

		if (appEnvChanged) {
			dispatch(UserActions.signOut());
			return;
		}

		if (
			AuthUtils.isUserLoggedIn() &&
			!AuthUtils.isMultiFactorMandatedButNotEnabled()
		) {
			await dispatch(FleetActions.getAllFleets.refresh());
			const scope = UserSelectors.selectCurrentScope(getState());

			if (scope?.fleetId === Flags.DEMO_FLEET_ID) {
				const loggedInUser = UserSelectors.selectLoggedInUser(store.getState());

				scope.fleetId = loggedInUser?.fleetId || "";
				scope.group = loggedInUser?.group;

				dispatch(
					setScope({
						fleetId: loggedInUser?.fleetId,
						group: loggedInUser?.group,
						rootGroupId: loggedInUser?.rootGroup,
					}),
				);
			}

			if (!scope.group && !AuthUtils.canAccessRoot()) {
				const userGroup = UserSelectors.selectLoggedInUserGroupId(getState());
				if (scope?.fleetId !== Flags.DEMO_FLEET_ID && userGroup) {
					scope.group = userGroup;
					dispatch(GroupsActions.setScopeGroup(userGroup));
				}
			}
			await dispatch(getFleetData({ ...scope, forceReload: true }));
		}

		// get user object
		dispatch(UserActions.refreshUserObject({ keepAuth: false }));
	},
);

export const updateDriver = asyncActionCreators<
	{ user: User; data: Partial<User> },
	User
>("[GLOBAL] UPDATE_DRIVER", async (action, dispatch, getState) => {
	const { user, data } = action.payload;
	const userId = user.uid;
	const updateData: any = { ...user, ...data };

	if (!updateData.uid) {
		updateData.originalEmail = user.email;
	}

	if (!userId) {
		dispatch(updateDriver.resolved(user));
		return user;
	}

	API_DEF.clearCache(new RegExp(userId, "g"));
	const res = await postUserUpdate.submit({
		params: { userId: userId },
		body: data,
	});
	return res.data;
});
